import React, { useState } from 'react';
import { ChevronRight, Flag, Home, Users as UsersIcon, ClipboardList, BookOpen, MapPin, Award, Plus, Check } from '../../components/CustomIcons';
import { Btn, Chip, Mono, ProgressBar, ScreenHeader } from '../Prototype';
import { StatCard, SectionTitle, StaffShell, Avatar, Badge } from './shared';
import { useUI } from './shared';
import { CM_IDENTITY, LEARNERS, CM_ACTIVITY, REENTRY_CHECKLIST } from './data';

const NAV = [
  { key: 'dashboard', label: 'Dashboard', icon: Home },
  { key: 'learners', label: 'Learners', icon: UsersIcon },
  { key: 'reentry', label: 'Reentry Plans', icon: ClipboardList },
  { key: 'resources', label: 'Resources', icon: BookOpen },
];

const useCMState = () => {
  const [state, setState] = useState(() => {
    try {
      const raw = localStorage.getItem('assistivos_casemanager_state');
      if (raw) return JSON.parse(raw);
    } catch (_e) { /* ignore */ }
    return { tab: 'dashboard', selectedLearnerId: null, notesByLearner: {}, flags: {}, checklist: {} };
  });
  const update = (patch) => setState((s) => {
    const next = { ...s, ...(typeof patch === 'function' ? patch(s) : patch) };
    try { localStorage.setItem('assistivos_casemanager_state', JSON.stringify(next)); } catch (_e) { /* ignore */ }
    return next;
  });
  return [state, update];
};

const CM1_Dashboard = ({ state, update }) => (
  <>
    <ScreenHeader title={CM_IDENTITY.facilityShort} subtitle={`Case Manager — ${CM_IDENTITY.name}`} />
    <main className="overflow-y-auto flex-1 px-6 py-5 space-y-5">
      <section aria-label="Caseload summary" className="grid grid-cols-2 gap-3">
        <StatCard label="Active Learners" value={CM_IDENTITY.activeLearners} testId="cm-stat-active" />
        <StatCard label="Releases in 30 Days" value={3} accent testId="cm-stat-releases" />
        <StatCard label="At-Risk Flags" value={2} accent testId="cm-stat-flags" />
        <StatCard label="Credentials This Month" value={6} testId="cm-stat-credentials" />
      </section>
      <section aria-label="Quick actions" className="grid grid-cols-1 gap-2">
        <Btn variant="ghost" onClick={() => update({ tab: 'learners' })} data-testid="cm-view-learners">View All Learners</Btn>
      </section>
      <section aria-label="Recent activity">
        <SectionTitle right="Last 24 hours">Learner Activity</SectionTitle>
        <ul className="list-none p-0 space-y-2">
          {CM_ACTIVITY.map((a, i) => (
            <li key={i} className="rounded-[12px] bg-[#f1f5f9] p-3 flex items-start gap-3">
              <Avatar name={LEARNERS.find((l) => l.id === a.learnerId)?.firstName + ' ' + LEARNERS.find((l) => l.id === a.learnerId)?.lastInitial} size={32} tone={a.tone === 'orange' ? 'orange' : 'navy'} />
              <div className="flex-1 min-w-0">
                <button
                  onClick={() => update({ tab: 'learners', selectedLearnerId: a.learnerId, view: 'detail' })}
                  data-testid={`cm-activity-${a.learnerId}`}
                  className="text-left text-[14px] text-[#0f2744] font-semibold hover:underline"
                >
                  {a.text}
                </button>
                <div className="text-[12px] text-[#64748b] mt-0.5">{a.when}</div>
              </div>
              {a.tone === 'orange' && <Badge tone="orange">Flag</Badge>}
              {a.tone === 'success' && <Badge tone="success">Earned</Badge>}
            </li>
          ))}
        </ul>
      </section>
    </main>
  </>
);

const CM2_Learners = ({ update }) => {
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('all');
  const filtered = LEARNERS.filter((l) => {
    if (filter === 'at-risk' && l.status !== 'at-risk') return false;
    if (filter === 'release-soon' && l.status !== 'release-soon') return false;
    if (filter === 'active' && l.status !== 'active') return false;
    if (q && !`${l.firstName} ${l.lastInitial} ${l.module}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });
  return (
    <>
      <ScreenHeader title="Learners" subtitle={`${CM_IDENTITY.activeLearners} in your caseload`} />
      <main className="overflow-y-auto flex-1 px-6 py-5 space-y-4">
        <input
          type="text"
          placeholder="Search learners by name or module..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="cm-search"
          className="w-full min-h-[48px] rounded-[12px] border border-[#e2e8f0] bg-[#f1f5f9] px-4 text-[14px] focus:outline-none focus:border-[#f97316]"
          aria-label="Search learners"
        />
        <div className="flex gap-2 flex-wrap" role="tablist" aria-label="Filter learners">
          {[
            { k: 'all', l: 'All' },
            { k: 'at-risk', l: 'At-Risk' },
            { k: 'release-soon', l: 'Release Soon' },
            { k: 'active', l: 'Active' },
          ].map((f) => (
            <button
              key={f.k}
              role="tab"
              aria-selected={filter === f.k}
              onClick={() => setFilter(f.k)}
              data-testid={`cm-filter-${f.k}`}
              className="rounded-full px-3 py-1.5 text-[13px] font-semibold"
              style={filter === f.k ? { background: '#f97316', color: '#0f2744' } : { background: '#f1f5f9', color: '#64748b' }}
            >
              {f.l}
            </button>
          ))}
        </div>
        <ul className="list-none p-0 space-y-3">
          {filtered.map((l) => (
            <li key={l.id}>
              <button
                onClick={() => update({ selectedLearnerId: l.id, view: 'detail' })}
                data-testid={`cm-learner-${l.id}`}
                className="w-full text-left rounded-[12px] bg-[#f1f5f9] hover:bg-white border border-[#e2e8f0] hover:border-[#0f2744] p-4 transition-all"
              >
                <div className="flex items-start gap-3">
                  <Avatar name={`${l.firstName} ${l.lastInitial}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="p-display text-[17px] leading-tight" style={{ color: '#0f2744' }}>{l.firstName} {l.lastInitial}</div>
                      {l.status === 'at-risk' && <Badge tone="orange">At-Risk</Badge>}
                      {l.status === 'release-soon' && <Badge tone="orange">Release Soon</Badge>}
                      {l.status === 'active' && <Badge tone="success">Active</Badge>}
                    </div>
                    <div className="text-[13px] text-[#64748b] mt-0.5">{l.module}</div>
                    <div className="mt-2"><ProgressBar value={l.progress} ariaLabel={`${l.progress}% complete`} /></div>
                    <div className="mt-1 flex items-center justify-between text-[12px] text-[#64748b]">
                      <span><Mono>{l.progress}</Mono>% complete</span>
                      <span>Release <Mono>{l.releaseDate}</Mono></span>
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-[#94a3b8] mt-1" aria-hidden="true" />
                </div>
              </button>
            </li>
          ))}
        </ul>
      </main>
    </>
  );
};

const CM3_LearnerDetail = ({ state, update }) => {
  const l = LEARNERS.find((x) => x.id === state.selectedLearnerId);
  const { showToast, showModal } = useUI();
  const [note, setNote] = useState('');
  if (!l) return null;
  const notes = state.notesByLearner[l.id] || [
    { when: '3 days ago', text: 'Discussed housing options post-release. Referred to Collin County Housing Authority.' },
    { when: '1 week ago', text: 'Learner expressed interest in Lone Star Energy Partners. Added to reentry plan.' },
    { when: '3 weeks ago', text: 'Initial intake completed.' },
  ];
  const addNote = () => {
    if (!note.trim()) return;
    update((s) => ({
      notesByLearner: { ...s.notesByLearner, [l.id]: [{ when: 'Just now', text: note.trim() }, ...(s.notesByLearner[l.id] || notes)] },
    }));
    setNote('');
    showToast('Note added to learner file', 'success');
  };
  const flagAtRisk = () => showModal({
    title: `Flag ${l.firstName} ${l.lastInitial} as at-risk?`,
    body: 'This will notify their instructor and TTI admin. The learner will appear in the at-risk queue until resolved.',
    confirmLabel: 'Send Flag',
    onConfirm: () => {
      update((s) => ({ flags: { ...s.flags, [l.id]: true } }));
      showToast(`${l.firstName} ${l.lastInitial} flagged. Instructor and TTI Admin notified.`, 'success');
    },
  });
  return (
    <>
      <ScreenHeader
        title={`${l.firstName} ${l.lastInitial}`}
        subtitle={`ID ${l.facilityId} · Release ${l.releaseDate}`}
        onBack={() => update({ view: null })}
        breadcrumbs={[
          { label: 'Dashboard', onClick: () => update({ tab: 'dashboard', view: null }) },
          { label: 'Learners', onClick: () => update({ tab: 'learners', view: null }) },
          { label: `${l.firstName} ${l.lastInitial}` },
        ]}
      />
      <main className="overflow-y-auto flex-1 px-6 py-5 space-y-5">
        <section aria-label="Progress overview">
          <SectionTitle>Progress</SectionTitle>
          <div className="rounded-[12px] bg-[#f1f5f9] p-4">
            <div className="text-[14px] font-semibold text-[#0f2744]">{l.module} — <Mono>{l.progress}</Mono>% complete</div>
            <div className="mt-2"><ProgressBar value={l.progress} /></div>
            <div className="mt-3 flex flex-wrap gap-2">
              {l.credentialsEarned.map((c) => (
                <Chip key={c.name}><Award className="w-3 h-3" /> {c.name} <Mono>{c.pct}</Mono>%</Chip>
              ))}
              {l.credentialsInProgress.map((c) => (
                <Chip key={c.name}>{c.name} <Mono>{c.pct}</Mono>%</Chip>
              ))}
            </div>
          </div>
        </section>
        <section aria-label="Case notes">
          <SectionTitle right={`${notes.length} entries`}>Case Notes</SectionTitle>
          <div className="flex items-start gap-2">
            <input
              type="text"
              placeholder="Add a note..."
              value={note}
              onChange={(e) => setNote(e.target.value)}
              data-testid="cm-note-input"
              className="flex-1 min-h-[48px] rounded-[12px] border border-[#e2e8f0] bg-[#f1f5f9] px-4 text-[14px] focus:outline-none focus:border-[#f97316]"
            />
            <Btn onClick={addNote} data-testid="cm-note-add" className="px-4"><Plus className="w-4 h-4" aria-hidden="true" /> Add</Btn>
          </div>
          <ul className="mt-3 list-none p-0 space-y-2">
            {notes.map((n, i) => (
              <li key={i} className="rounded-[12px] bg-[#f1f5f9] p-3">
                <div className="text-[12px] text-[#64748b] font-semibold uppercase tracking-widest">{n.when}</div>
                <div className="text-[14px] text-[#1c1c1e] mt-1 leading-relaxed">{n.text}</div>
              </li>
            ))}
          </ul>
        </section>
        <section aria-label="Actions" className="grid grid-cols-2 gap-2">
          <Btn variant="ghost" onClick={() => update({ view: 'reentry' })} data-testid="cm-view-reentry">View Reentry Plan</Btn>
          <Btn variant="ghost" onClick={flagAtRisk} data-testid="cm-flag" aria-label={`Flag ${l.firstName} as at-risk`}>
            <Flag className="w-4 h-4" aria-hidden="true" /> Flag At-Risk
          </Btn>
        </section>
      </main>
    </>
  );
};

const CM4_Reentry = ({ state, update }) => {
  const l = LEARNERS.find((x) => x.id === state.selectedLearnerId) || LEARNERS[0];
  const { showToast } = useUI();
  const checklist = state.checklist[l.id] || REENTRY_CHECKLIST.reduce((acc, i) => ({ ...acc, [i.key]: i.default }), {});
  const toggle = (key) => update((s) => ({
    checklist: {
      ...s.checklist,
      [l.id]: { ...(s.checklist[l.id] || checklist), [key]: !(s.checklist[l.id] || checklist)[key] },
    },
  }));
  const doneCount = Object.values(checklist).filter(Boolean).length;
  return (
    <>
      <ScreenHeader
        title="Reentry Plan"
        subtitle={`${l.firstName} ${l.lastInitial} · ${l.daysToRelease} days to release`}
        onBack={() => update({ view: 'detail' })}
        breadcrumbs={[
          { label: 'Dashboard', onClick: () => update({ tab: 'dashboard', view: null }) },
          { label: 'Learners', onClick: () => update({ tab: 'learners', view: null }) },
          { label: `${l.firstName} ${l.lastInitial}`, onClick: () => update({ view: 'detail' }) },
          { label: 'Reentry Plan' },
        ]}
      />
      <main className="overflow-y-auto flex-1 px-6 py-5 space-y-5">
        <section aria-label="Pre-release checklist">
          <SectionTitle right={`${doneCount} of ${REENTRY_CHECKLIST.length} complete`}>Pre-Release Checklist</SectionTitle>
          <ul className="list-none p-0 space-y-2">
            {REENTRY_CHECKLIST.map((item) => {
              const done = checklist[item.key];
              return (
                <li key={item.key}>
                  <button
                    onClick={() => toggle(item.key)}
                    data-testid={`cm-check-${item.key}`}
                    aria-pressed={done}
                    className="w-full text-left rounded-[12px] px-4 py-3 flex items-center gap-3"
                    style={{ background: done ? 'rgba(21,128,61,0.10)' : '#f1f5f9', border: `1px solid ${done ? '#15803d' : 'transparent'}` }}
                  >
                    <span
                      className="w-6 h-6 rounded-md flex items-center justify-center shrink-0"
                      style={{ background: done ? '#15803d' : '#ffffff', border: `1px solid ${done ? '#15803d' : '#cbd5e1'}` }}
                      aria-hidden="true"
                    >
                      {done && <Check className="w-4 h-4 text-white" />}
                    </span>
                    <span className={`text-[14px] ${done ? 'font-semibold' : ''}`} style={{ color: '#0f2744' }}>{item.label}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
        <section aria-label="Post-release milestones">
          <SectionTitle>Post-Release Milestones</SectionTitle>
          <div className="grid grid-cols-1 gap-2">
            {[
              { t: 'Release Day', d: 'Credential wallet transferred. Reentry resource kit delivered.' },
              { t: '30 Days', d: 'First employer check-in. Housing confirmed. Benefits active.' },
              { t: '90 Days', d: 'Employment status verified. Wage milestone tracked.' },
            ].map((m) => (
              <div key={m.t} className="rounded-[12px] bg-[#f1f5f9] p-3">
                <div className="text-[11px] font-bold uppercase tracking-widest" style={{ color: '#f97316' }}>{m.t}</div>
                <div className="text-[14px] text-[#0f2744] mt-1 leading-relaxed">{m.d}</div>
              </div>
            ))}
          </div>
        </section>
        <section aria-label="Employer match">
          <div className="rounded-[12px] p-4" style={{ background: 'rgba(15,39,68,0.06)' }}>
            <div className="text-[14px] text-[#0f2744]">{l.firstName} {l.lastInitial} is matched with <Mono>4</Mono> employers. Highest match: <strong>Lone Star Energy Partners</strong>.</div>
            <Btn variant="ghost" className="mt-3 w-full" onClick={() => showToast('Employer contact request sent to TTI liaison.')} data-testid="cm-contact-employer">Contact Employer</Btn>
          </div>
        </section>
        <Btn onClick={() => showToast('Reentry plan exported as PDF.', 'success')} className="w-full" data-testid="cm-download-plan">Download Plan</Btn>
      </main>
    </>
  );
};

const CM_STAFF_BADGE = { initial: 'M', name: CM_IDENTITY.name, role: 'Case Manager' };

const CaseManager = () => {
  const [state, update] = useCMState();
  const tab = state.tab || 'dashboard';
  const view = state.view;
  const activeKey = view ? 'learners' : tab;
  const onNav = (k) => update({ tab: k, view: null });

  let screen;
  if (view === 'detail') screen = <CM3_LearnerDetail state={state} update={update} />;
  else if (view === 'reentry') screen = <CM4_Reentry state={state} update={update} />;
  else if (tab === 'learners') screen = <CM2_Learners state={state} update={update} />;
  else if (tab === 'reentry') {
    const selected = state.selectedLearnerId || LEARNERS.find((l) => l.status === 'at-risk')?.id || LEARNERS[0].id;
    screen = <CM4_Reentry state={{ ...state, selectedLearnerId: selected }} update={update} />;
  } else if (tab === 'resources') {
    screen = (
      <>
        <ScreenHeader title="Resources" subtitle="Case Manager toolkit" />
        <main className="overflow-y-auto flex-1 px-6 py-5 space-y-3">
          {[
            { t: 'Reentry Playbook (2025)', d: 'Full policy manual for TTI-participating facilities.' },
            { t: 'Employer Partner Directory', d: '38 verified second-chance employers.' },
            { t: 'Benefits Referral Guide', d: 'SNAP, Medicaid, workforce services enrollment steps.' },
          ].map((it) => (
            <div key={it.t} className="rounded-[12px] bg-[#f1f5f9] p-4">
              <div className="p-display text-[16px]" style={{ color: '#0f2744' }}>{it.t}</div>
              <div className="text-[13px] text-[#64748b] mt-1">{it.d}</div>
            </div>
          ))}
        </main>
      </>
    );
  } else {
    screen = <CM1_Dashboard state={state} update={update} />;
  }

  return (
    <StaffShell items={NAV} activeKey={activeKey} onChange={onNav} badge={CM_STAFF_BADGE}>
      {screen}
    </StaffShell>
  );
};

export default CaseManager;
